import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [taskStats, setTaskStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState({
        start_date: '',
        end_date: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [revenueResponse, taskResponse] = await Promise.all([
                api.getRevenueStats(dateFilter),
                api.getTaskStats()
            ]);

            if (revenueResponse.data.success) {
                setStats(revenueResponse.data);
            }
            if (taskResponse.data.success) {
                setTaskStats(taskResponse.data);
            }
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        setDateFilter({
            ...dateFilter,
            [e.target.name]: e.target.value
        });
    };

    const applyFilter = () => {
        loadData();
    };

    if (loading) {
        return <div className="loading">Loading dashboard...</div>;
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount || 0);
    };

    return (
        <div className="dashboard">
            <div className="page-header">
                <h1>Revenue Dashboard</h1>
                <div className="filter-controls">
                    <input
                        type="date"
                        name="start_date"
                        value={dateFilter.start_date}
                        onChange={handleFilterChange}
                        placeholder="Start Date"
                    />
                    <input
                        type="date"
                        name="end_date"
                        value={dateFilter.end_date}
                        onChange={handleFilterChange}
                        placeholder="End Date"
                    />
                    <button onClick={applyFilter} className="btn-primary">
                        Apply Filter
                    </button>
                    {(dateFilter.start_date || dateFilter.end_date) && (
                        <button 
                            onClick={() => {
                                setDateFilter({ start_date: '', end_date: '' });
                                setTimeout(loadData, 100);
                            }} 
                            className="btn-secondary"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Revenue Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card revenue-paid">
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <h3>Total Paid</h3>
                        <p className="stat-value">{formatCurrency(stats?.stats?.total_paid)}</p>
                        <small>{stats?.stats?.paid_count || 0} payments</small>
                    </div>
                </div>

                <div className="stat-card revenue-pending">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-content">
                        <h3>Pending</h3>
                        <p className="stat-value">{formatCurrency(stats?.stats?.total_pending)}</p>
                        <small>{stats?.stats?.pending_count || 0} pending</small>
                    </div>
                </div>

                <div className="stat-card revenue-overdue">
                    <div className="stat-icon">⚠️</div>
                    <div className="stat-content">
                        <h3>Overdue</h3>
                        <p className="stat-value">{formatCurrency(stats?.stats?.total_overdue)}</p>
                        <small>{stats?.stats?.overdue_count || 0} overdue</small>
                    </div>
                </div>

                <div className="stat-card tasks-summary">
                    <div className="stat-icon">✓</div>
                    <div className="stat-content">
                        <h3>Active Tasks</h3>
                        <p className="stat-value">{taskStats?.stats?.in_progress || 0}</p>
                        <small>{taskStats?.stats?.total || 0} total tasks</small>
                    </div>
                </div>
            </div>

            {/* Monthly Revenue Chart */}
            <div className="chart-container">
                <h2>Monthly Revenue Trend</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats?.monthlyRevenue || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line 
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#667eea" 
                            strokeWidth={2}
                            name="Revenue"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="dashboard-grid">
                {/* Top Clients */}
                <div className="card">
                    <h2>Top Clients by Revenue</h2>
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Revenue</th>
                                    <th>Payments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats?.clientRevenue?.slice(0, 10).map((client) => (
                                    <tr key={client.id}>
                                        <td>
                                            <strong>{client.client_name}</strong>
                                            {client.company_name && (
                                                <small> ({client.company_name})</small>
                                            )}
                                        </td>
                                        <td className="revenue-cell">
                                            {formatCurrency(client.total_revenue)}
                                        </td>
                                        <td>{client.payment_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Revenue by Service */}
                <div className="card">
                    <h2>Revenue by Service</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={stats?.serviceRevenue || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="service_name" angle={-45} textAnchor="end" height={100} />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Bar dataKey="total_revenue" fill="#764ba2" name="Revenue" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Employee Task Overview */}
            {taskStats?.tasksByEmployee && taskStats.tasksByEmployee.length > 0 && (
                <div className="card">
                    <h2>Employee Task Overview</h2>
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Total Tasks</th>
                                    <th>Completed</th>
                                    <th>Pending</th>
                                    <th>Completion Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {taskStats.tasksByEmployee.map((emp) => {
                                    const completionRate = emp.total_tasks > 0 
                                        ? ((emp.completed_tasks / emp.total_tasks) * 100).toFixed(0)
                                        : 0;
                                    return (
                                        <tr key={emp.id}>
                                            <td><strong>{emp.full_name}</strong></td>
                                            <td>{emp.total_tasks}</td>
                                            <td className="completed">{emp.completed_tasks}</td>
                                            <td className="pending">{emp.pending_tasks}</td>
                                            <td>
                                                <div className="progress-bar">
                                                    <div 
                                                        className="progress-fill" 
                                                        style={{ width: `${completionRate}%` }}
                                                    >
                                                        {completionRate}%
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard;
